import { css, html, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { createRef, ref, Ref } from 'lit/directives/ref.js';
import type Uppy from '@uppy/core'
import { useEffect } from 'haunted'
import { LitHauntedElement } from '../../mixins/lit-haunted-element';
import { FileReferenceAPI } from './FileReference';
import { isNullishOrWhitespace } from '../../functions/nullish';
import { IntervalController } from '../../controllers/interval-controller';

type UploadMode = 'single' | 'multiple' | 'uninitialized'

@customElement('ep-async-file-uploader')
export class EPAsyncFileUploader extends LitHauntedElement {
  private uppyContainerRef: Ref<HTMLElement> = createRef();

  @property({ attribute: 'api-origin', reflect: false })
  apiOrigin: string = window.origin;

  @property({ attribute: 'file-reference', reflect: true })
  fileReferenceID?: string = '';

  @property({ attribute: 'file-reference-group', reflect: true })
  fileReferenceGroupID?: string = '';

  @property({ type: Boolean, attribute: 'show-existing-files', reflect: true })
  showExistingFiles: boolean = false;

  get mode(): UploadMode {
    if (!isNullishOrWhitespace(this.fileReferenceID)) {
      return 'single'
    }
    else if (!isNullishOrWhitespace(this.fileReferenceGroupID)) {
      return 'multiple'
    }
    return 'uninitialized';
  }

  // Used to keep the session alive during long running uploads
  public sessionKeepAliveInterval = new IntervalController(this, () => {
    (async () => {
      try {
        if (this.provider) {
          await this.provider.Ping();
        }
      }
      catch {}
    })();
  }, 120 * 1000); // 2 minutes

  // -----------------

  @state()
  provider?: FileReferenceAPI;

  private uppy?: Uppy | null = undefined;

  //#region Event Handlers

  private async _updateUppyRestrictions() {
    if (!this.provider) return;
    if (!this.uppy) return;
    
    const setDashboardDisabled = (disabled: boolean) => {
      if (this.uppy) {
        const { plugins } = this.uppy.getState();
        if (plugins['Dashboard']) {
          plugins['Dashboard'].disabled = disabled;
        }
        this.uppy.setState({
          plugins: {
            ...plugins,
          }
        })
      }
    }
    
    if (this.mode === 'single') {
      setDashboardDisabled(false);
      this.uppy.setOptions({
        ...this.uppy.opts,
        restrictions: {
          minFileSize: 1,
          maxFileSize: null,
          minNumberOfFiles: 1,
          maxNumberOfFiles: 1,
        }
      });
    }
    else if (this.mode === 'multiple') {
      setDashboardDisabled(false);
      // Get information about the group and set 
      const groupMeta = await this.provider.GetGroupMetadata(this.fileReferenceGroupID ?? '');
      this.uppy.setOptions({
        ...this.uppy.opts,
        restrictions: {
          minFileSize: 1,
          maxFileSize: groupMeta.FileSizeLimit,
          minNumberOfFiles: 1,
          maxNumberOfFiles: groupMeta.RemainingFiles,
        }
      });

      // Pre-populate files that have already been uploaded (if we are configured to do so)
      if (this.showExistingFiles) {
        const existingFiles = await this.provider.GetManyFileReferenceMetadata(groupMeta.FileReferences);
        for(let fileMeta of existingFiles) {
          if (fileMeta.Status === 'Complete' && fileMeta.FileName !== null && fileMeta.EstimatedFileSize !== null) {
            // Based on: https://github.com/transloadit/uppy/issues/1112#issuecomment-432339569
            const fileID = this.uppy.addFile({
              name: fileMeta.FileName,
              lastModified: fileMeta.CreationDate?.valueOf() ?? undefined,
              meta: {
                //name: fakeName,
                relativePath: `FR/${fileMeta.FileReferenceID}/${fileMeta.FileName}`,
                // Re-use author field to show file date and download link
                // author: fileMeta.CreationDate === null ? undefined : {
                //   name: '⬇️ ',
                //   url: fileMeta.ContentDownloadUrl,
                // },
              },
              data: {
                size: fileMeta.EstimatedFileSize
              },
              isRemote: true,
            });
            this.uppy.setFileState(fileID, {
              progress: {
                // @ts-ignore
                uploadStarted: true,
                uploadComplete: true,
              }
            });
          }
        }
      }
    }
    else {
      setDashboardDisabled(true);
    }
  }

  //#endregion

  //#region Renders

  render() {    
    //#region Effects

    // Whenever the API URL is updated, create a new provider
    useEffect(() => {
      try {
        const url = new URL(this.apiOrigin, window.origin);
        this.provider = new FileReferenceAPI(url);
      }
      catch {}
    }, [this.apiOrigin]);

    useEffect(async () => {
      if (!this.provider) return;
      if (!this.uppyContainerRef.value) return;
      if (this.uppy !== undefined) return;
      this.uppy = null;

      const { Uppy } = await import('@uppy/core');
      const { default: Dashboard } = await import('@uppy/dashboard');
      const { FileReferenceUppyPlugin } = await import('./FileReferenceUppyPlugin');
      const stylesUppy = await import('@uppy/core/dist/style.css?inline');
      const stylesUppyDashboard = await import('@uppy/dashboard/dist/style.css?inline');

      if (this.renderRoot instanceof ShadowRoot) {
        this.renderRoot.adoptedStyleSheets = [
          ...this.renderRoot.adoptedStyleSheets,
          unsafeCSS(stylesUppy.default).styleSheet!,
          unsafeCSS(stylesUppyDashboard.default).styleSheet!,
        ];
      }

      this.uppy = new Uppy({
        debug: true,
        autoProceed: false,
        allowMultipleUploadBatches: true,
        restrictions: {
          minFileSize: 0,
          maxFileSize: 0,
          minNumberOfFiles: 0,
          maxNumberOfFiles: 0,
        }
        //@ts-ignore Consider removing once this has fixed and we can update our @uppy/* dependencies: https://github.com/transloadit/uppy/issues/5585
      }).use(Dashboard, {
        target: this.uppyContainerRef.value,
        inline: true,
        showProgressDetails: true,
        proudlyDisplayPoweredByUppy: false,
        disabled: false,
      }).use(FileReferenceUppyPlugin, {
        provider: this.provider,
        getHost: () => this,
      });

      await this._updateUppyRestrictions();
    }, [this.provider, this.uppyContainerRef]);

    useEffect(async () => {
      await this._updateUppyRestrictions();
    }, [this.fileReferenceID, this.fileReferenceGroupID])

    //#endregion

    //#region Main Render

    return html`
      <div class="container" ${ref(this.uppyContainerRef)}></div>
    `
    //#endregion
  }

  //#endregion

  //#region Styles
  static styles = [css`
    :host {
      display: block;
      width: auto;
      font-family: var(--bs-body-font-family);
      font-size: var(--bs-body-font-size);
      font-weight: var(--bs-body-font-weight);
      line-height: var(--bs-body-line-height);
      color: var(--bs-body-color);
      --font-mono: ui-monospace, SFMono-Regular, SF Mono, Menlo, "Cascadia Mono", Consolas, Liberation Mono, monospace;
    }
    .container {
      display: grid;
      grid-template-columns: 1fr;
      grid-template-rows: 1fr;
      height: 100%;
    }
    .uppy-Dashboard {
      height: 100%;
    }
    .uppy-Dashboard-inner {
      /* width: 750px;
      height: 550px; */
      width: auto!important;
      height: 100%!important;
    }
  `]
  //#endregion
}

declare global {
  interface HTMLElementTagNameMap {
    'ep-async-file-uploader': EPAsyncFileUploader
  }
}
